import React, { CSSProperties } from 'react'
import { Box } from '@mui/system'
import { cx } from '@emotion/css'
import { getClassName, parseClassNames } from '../../utils'
import { CssStyles, IDraggable } from '../../types'

export interface IContainerCondition {
  key: string
  visible?: boolean | ((...args: unknown[]) => boolean)
}

export interface IContainer extends IDraggable {
  /** Conditions for content to render */
  conditions: IContainerCondition[]

  /** Content of the container */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any

  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<'root'>

  /**
   * The ARIA role attribute of the element.
   */
  role?: string

  /** Provide an optional function to be called when the container is clicked */
  onClick?: (event: React.MouseEvent) => void

  /** Provide an optional function to be called when the context menu is opened */
  onContextMenu?: (event: React.MouseEvent) => void

  /** Render as React Fragment. Disables styling, role and onClick */
  asFragment?: boolean

  style?: CSSProperties | string
}

export const Container = React.forwardRef<HTMLDivElement, IContainer>(
  (
    {
      conditions,
      styles,
      children: rawChildren,
      role,
      onClick,
      onContextMenu,
      asFragment,
      draggable,
      onDrag,
      onDragStart,
      onDragEnd,
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
      style,
      ...props
    },
    ref
  ) => {
    const classNames = styles && parseClassNames(styles)

    const isContentVisible = (
      condition?: boolean | ((...args: unknown[]) => boolean)
    ): boolean =>
      condition
        ? typeof condition === 'function'
          ? condition()
          : condition
        : false

    const children =
      Array.isArray(conditions) && conditions.length > 0
        ? conditions?.map((condition) => {
            return isContentVisible(condition.visible) ? (
              <React.Fragment key={condition.key}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {props[`${condition.key}`] as any}
              </React.Fragment>
            ) : null
          })
        : rawChildren

    return asFragment ? (
      <>{children}</>
    ) : (
      <Box
        ref={ref}
        className={cx(classNames?.root, getClassName(style))}
        component="div"
        onClick={onClick}
        onContextMenu={onContextMenu}
        draggable={draggable}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        {...(role ? { role } : {})}
      >
        {children}
      </Box>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  Container.displayName = 'Container'
}
